import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { usePlatformAuthStore } from '@/stores/platformAuth.store'
import { usePermissionsStore } from '@/stores/permissions.store'

export function installGuards(router: Router): void {
  router.beforeEach((to) => {
    // EReach Admin has its own, separate auth context — a platform staff
    // session never implies a tenant session or vice versa.
    if (to.meta.requiresPlatformAuth || to.name === 'platform-login') {
      const platformAuth = usePlatformAuthStore()
      if (to.meta.requiresPlatformAuth && !platformAuth.isAuthenticated) {
        return { name: 'platform-login', query: { redirect: to.fullPath } }
      }
      if (to.name === 'platform-login' && platformAuth.isAuthenticated) {
        return { name: 'platform-dashboard' }
      }
      return
    }

    const auth = useAuthStore()
    if (to.meta.requiresAuth && !auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
    if ((to.name === 'login' || to.name === 'register') && auth.isAuthenticated) {
      return { name: auth.user?.onboardingCompleted ? 'dashboard' : 'onboarding' }
    }

    // A freshly created company lands in onboarding until it's finished (or
    // skipped) — nothing else in the app is reachable before that.
    if (to.meta.requiresAuth && auth.user && !auth.user.onboardingCompleted && !to.meta.allowBeforeOnboarding) {
      return { name: 'onboarding' }
    }
    if (to.name === 'onboarding' && auth.user?.onboardingCompleted) {
      return { name: 'dashboard' }
    }

    // Role gate: same rule the nav uses to hide items, enforced for deep links.
    if (to.meta.permission && !usePermissionsStore().can(to.meta.permission)) {
      return { name: 'forbidden', query: { from: to.fullPath } }
    }
  })

  router.afterEach((to) => {
    document.title = to.meta.title ? `${to.meta.title} · EReach` : 'EReach'
  })
}
